import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Database, Key, Loader2, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Settings → Integrations → Terra (AI Assistant). Terra is powered by a
 * super admin's own OpenAI API key (this account is paid/managed separately
 * — Terra intentionally does not draw on the shared Emergent-managed key).
 * The model name is also configurable here since different OpenAI
 * accounts/projects have access to different models.
 */
export const TerraIntegrationSettings = () => {
  const [status, setStatus] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [modelInput, setModelInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/ai-assistant/api-key-status`);
      setStatus(res.data);
      setModelInput(res.data?.model || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Terra integration status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const saveKey = async () => {
    if (!keyInput.trim()) return;
    setSaving(true);
    try {
      const res = await api.put(`${API}/ai-assistant/api-key`, { api_key: keyInput.trim(), model: modelInput.trim() });
      setStatus(res.data);
      setKeyInput('');
      toast.success('Terra OpenAI settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const saveModelOnly = async () => {
    setSaving(true);
    try {
      const res = await api.put(`${API}/ai-assistant/model`, { model: modelInput.trim() });
      setStatus(res.data);
      toast.success('Model updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update model');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="terra-integration-settings">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <Database className="w-4 h-4" style={{ color: '#ee7203' }} />
        Terra (AI Assistant)
      </h3>
      <p className="text-sm text-gray-500">
        Terra uses your own OpenAI API key and account — it never uses shared platform credits.
        Set the exact model name your OpenAI project has access to below (e.g. gpt-4o, gpt-4o-mini,
        gpt-4.1) — if chat fails with a "model not found / no access" error, your project doesn't
        have that model enabled; change it here to one your account supports. Grant individual
        users chat access under Settings &gt; User Admin.
      </p>

      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      ) : (
        <Card>
          <CardContent className="py-3 px-4 space-y-3">
            {status?.configured ? (
              <div className="text-sm text-green-700 flex items-center gap-1.5" data-testid="terra-key-configured-status">
                <CheckCircle2 className="w-4 h-4" />
                OpenAI key active — model: <span className="font-mono">{status.model}</span>
                {status.updated_by_email && <span className="text-gray-400">— set by {status.updated_by_email}</span>}
              </div>
            ) : (
              <div className="text-sm text-gray-500 flex items-center gap-1.5">
                No OpenAI key configured yet — Terra chat won't work until one is saved.
              </div>
            )}
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="sk-... (your OpenAI API key)"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                className="text-sm"
                data-testid="terra-integration-api-key-input"
              />
            </div>
            <div className="flex gap-2 items-center">
              <Input
                type="text"
                placeholder="Model (e.g. gpt-4o-mini)"
                value={modelInput}
                onChange={(e) => setModelInput(e.target.value)}
                className="text-sm"
                data-testid="terra-integration-model-input"
              />
              {status?.configured && (
                <Button size="sm" variant="outline" onClick={saveModelOnly} disabled={saving || !modelInput.trim()} data-testid="terra-integration-save-model-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update model'}
                </Button>
              )}
            </div>
            <Button size="sm" onClick={saveKey} disabled={saving || !keyInput.trim()} data-testid="terra-integration-save-key-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Key className="w-3.5 h-3.5 mr-1" /> Save key + model</>}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
